// Mock editorial + system data for the redesigned admin portal.
// Live until the articles / audit-log backend lands (Stage 3 of the
// admin portal plan). Shape mirrors the design-handoff `admin-data.jsx`
// so the eventual switch to real data is field-for-field.

import type { KindKey, ReviewStateKey } from "@/components/admin-ui";

export type AuthorKey = "clarissa" | "marcus" | "priya" | "jianwei" | "faridah";

export type Author = {
  name: string;
  initials: string;
  tint: string;
  team: string;
};

export const AUTHORS: Record<AuthorKey, Author> = {
  clarissa: { name: "Clarissa Tan",  initials: "CT", tint: "#1d4a3a", team: "Editorial" },
  marcus:   { name: "Marcus Lim",    initials: "ML", tint: "#1a3e6a", team: "Markets desk" },
  priya:    { name: "Priya Raj",     initials: "PR", tint: "#6b5320", team: "Policy desk" },
  jianwei:  { name: "Jian Wei Tan",  initials: "JW", tint: "#3a2e7a", team: "Guides" },
  faridah:  { name: "Faridah Yusof", initials: "FY", tint: "#7a2d1f", team: "Editorial" },
};

// authorDisplay is the joined snippet from the article-list response —
// populated when articlesApiEnabled() is true. Pages prefer this over
// looking up `author` against the AUTHORS mock map, so a real auth_users
// UUID renders correctly without needing a parallel /admin/authors fetch.
export type AuthorDisplay = Author & { id: string; email?: string };

export type Article = {
  id: string;
  kind: KindKey;
  title: string;
  dek: string;
  // `author` is the lookup key — an AuthorKey in mock mode, a real
  // auth_users UUID in live mode. Use `authorDisplay` when present.
  author: AuthorKey;
  authorDisplay?: AuthorDisplay;
  state: ReviewStateKey;
  submitted: string;
  // submittedAt is the raw ISO timestamp (live mode only). The review
  // queue uses it to compute median wait + oldest age locally; mock
  // entries leave it undefined and those stats fall back to "—".
  submittedAt?: string;
  sla: "on-time" | "overdue" | "cleared";
  version: number;
  words: number;
  readTime: string;
  flags: number;
  comments: number;
  slug: string;
  reviewer: string | null;
  // reviewerId is the raw admin_users.id from articles.reviewer_id —
  // optional so the existing mock seed (which only carries display
  // strings) still type-checks. Pages compare it against the current
  // admin id to decide whether a row belongs in "Assigned to you".
  reviewerId?: string | null;
  tags: string[];
  views?: number;
  reads?: number;
  leads?: number;
  publishAt?: string;
};

export const ARTICLES: Article[] = [
  { id: "a-2026-05-13-a", kind: "URA",    title: "URA Draft Master Plan 2025: 5 areas that will reshape where you buy",
    dek: "Greater Southern Waterfront, Jurong Lake District, and three more — what each means for resale values over the next 10 years.",
    author: "clarissa", state: "awaiting_review", submitted: "38 minutes ago", sla: "on-time",
    version: 4, words: 1840, readTime: "6 min", flags: 2, comments: 0, slug: "/news/ura-master-plan-2025",
    reviewer: null, tags: ["URA Master Plan", "GSW", "JLD"] },
  { id: "a-2026-05-13-b", kind: "Policy", title: "MOP changes are coming — what HDB owners should know before 2027",
    dek: "The 5-year MOP rule is on the agenda again. Here is what is changing, who it affects, and the timeline buyers should plan around.",
    author: "priya",    state: "awaiting_review", submitted: "2 hours ago", sla: "on-time",
    version: 2, words: 1240, readTime: "4 min", flags: 0, comments: 0, slug: "/news/mop-changes-2027",
    reviewer: null, tags: ["HDB", "MOP", "Resale"] },
  { id: "a-2026-05-12-a", kind: "Market", title: "Q1 2026 private resale: PSF up 1.8% QoQ, volumes stay soft",
    dek: "Caveats lodged are flat, but median PSF crept up across CCR and RCR. Reading the divergence between price and volume.",
    author: "marcus",   state: "in_review", submitted: "Yesterday, 4:12 PM", sla: "on-time",
    version: 3, words: 1610, readTime: "5 min", flags: 1, comments: 3, slug: "/news/q1-2026-resale",
    reviewer: "You",    tags: ["Private resale", "PSF", "Q1 2026"] },
  { id: "a-2026-05-12-b", kind: "Guide",  title: "ABSD remission walk-through: matrimonial home, with worked example",
    dek: "A 3-page guide for SC couples with one existing property. Spreadsheet attached.",
    author: "jianwei",  state: "changes_requested", submitted: "2 days ago", sla: "overdue",
    version: 2, words: 2120, readTime: "7 min", flags: 4, comments: 7, slug: "/news/absd-matrimonial",
    reviewer: "You",    tags: ["ABSD", "IRAS", "Stamp duty"] },
  { id: "a-2026-05-11-a", kind: "Brief",  title: "May 2026 cooling-measure rumours — what we can verify, what we cannot",
    dek: "Three rumours are circulating. We checked each against URA, MAS, and IRAS sources.",
    author: "faridah",  state: "awaiting_review", submitted: "3 days ago", sla: "overdue",
    version: 1, words: 980, readTime: "3 min", flags: 3, comments: 0, slug: "/news/may-2026-rumours",
    reviewer: null, tags: ["Cooling measures", "Rumour"] },
  { id: "a-2026-05-10-a", kind: "Policy", title: "IRAS clarifies ABSD remission for matrimonial-home transfers",
    dek: "The 6-month deadline is being measured differently. Here is what changed and why it matters.",
    author: "priya",    state: "approved", submitted: "May 9, 2026", sla: "cleared",
    version: 3, words: 1180, readTime: "3 min", flags: 0, comments: 4, slug: "/news/iras-absd-clarify",
    reviewer: "Hwee Kim", tags: ["ABSD", "IRAS"] },
  { id: "a-2026-05-09-a", kind: "Market", title: "New launch take-up: 5 of 6 launches in March cleared 60% in opening weekend",
    dek: "Sales-day data and what it implies for pricing power in Q2.",
    author: "marcus",   state: "published", submitted: "May 8, 2026", sla: "cleared",
    version: 5, words: 1720, readTime: "6 min", flags: 0, comments: 8, slug: "/news/march-launches",
    reviewer: "Hwee Kim", tags: ["New launch", "Sell-through"],
    views: 8120, reads: 5470, leads: 24 },
  { id: "a-2026-05-07-a", kind: "Brief",  title: "En-bloc tracker: 4 collective sales launched in April, only 1 hit reserve",
    dek: "A running tally of collective sale outcomes, with our read on developer appetite.",
    author: "faridah",  state: "published", submitted: "May 6, 2026", sla: "cleared",
    version: 2, words: 720, readTime: "3 min", flags: 0, comments: 5, slug: "/news/enbloc-april",
    reviewer: "Hwee Kim", tags: ["En-bloc"],
    views: 4210, reads: 2890, leads: 11 },
  { id: "a-2026-05-05-a", kind: "Guide",  title: "CPF Ordinary Account interest rate review: 2.5% vs 2.7%",
    dek: "What a 20 bps change does to a 25-year amortisation, with worked examples.",
    author: "jianwei",  state: "scheduled", submitted: "May 4, 2026", sla: "cleared",
    version: 2, words: 1380, readTime: "5 min", flags: 0, comments: 2, slug: "/news/cpf-oa-rate",
    reviewer: "Hwee Kim", tags: ["CPF", "Interest rate"],
    publishAt: "May 18, 2026 · 8:00 AM" },
  { id: "a-2026-05-04-a", kind: "Market", title: "Rental market: HDB asking yields hold above 4.8% in the east",
    dek: "Tampines and Pasir Ris lead; central submarkets continue to soften from a high base.",
    author: "marcus",   state: "rejected", submitted: "May 3, 2026", sla: "cleared",
    version: 1, words: 980, readTime: "4 min", flags: 5, comments: 11, slug: "/news/rental-east",
    reviewer: "Hwee Kim", tags: ["Rental", "HDB"] },
];

export type ActivityEvent = {
  kind: "submitted" | "note" | "comment" | "flag-cleared";
  who: AuthorKey | "you" | "system";
  when: string;
  text: string;
  anchor?: string;
};

export const REVIEW_ACTIVITY: ActivityEvent[] = [
  { kind: "submitted",   who: "marcus",  when: "Yesterday · 4:12 PM",
    text: "Submitted v3 for review — addressed pricing-language flag from v2." },
  { kind: "note",        who: "you",     when: "Yesterday · 5:40 PM",
    text: "Claimed for review. Holding for URA cross-check on JLD GFA uplift." },
  { kind: "comment",     who: "you",     when: "Today · 9:08 AM",
    text: "Para 4 — “20% GFA uplift” needs a citation. URA factsheet 2025-04 page 11?",
    anchor: "§ Jurong Lake District" },
  { kind: "comment",     who: "marcus",  when: "Today · 9:34 AM",
    text: "Added citation; double-checked vs Q1 BTS report. Same number, different source page.",
    anchor: "§ Jurong Lake District" },
  { kind: "flag-cleared", who: "system", when: "Today · 9:35 AM",
    text: "Auto-check: 1 fact flag resolved (URA citation added). 0 open flags." },
];

export type EditorialCheck = { id: string; label: string; state: "pass" | "warn"; detail: string };
export const EDITORIAL_CHECKS: EditorialCheck[] = [
  { id: "links",     label: "External links",         state: "pass", detail: "7 of 7 resolve · last checked 12m ago" },
  { id: "citations", label: "Source citations",       state: "pass", detail: "4 sources, all primary (URA, MAS)" },
  { id: "numbers",   label: "Price & PSF figures",    state: "pass", detail: "14 numbers checked against /facts" },
  { id: "names",     label: "Project & person names", state: "warn", detail: "1 unverified — “Lentor Modern” spelling" },
  { id: "pii",       label: "PII scan",               state: "pass", detail: "No phone / NRIC / email leaks" },
  { id: "tone",      label: "Tone & house style",     state: "pass", detail: "Reading grade 11.4 · target 10–13" },
];

export const AUTHOR_STATS: Array<{ id: AuthorKey; drafts: number; awaiting: number; published: number; leads: number; views: number; last: string }> = [
  { id: "clarissa", drafts: 1, awaiting: 1, published: 4, leads: 86, views: 24820, last: "Today" },
  { id: "marcus",   drafts: 0, awaiting: 0, published: 6, leads: 71, views: 19100, last: "Today" },
  { id: "priya",    drafts: 0, awaiting: 1, published: 5, leads: 64, views: 14820, last: "Today" },
  { id: "jianwei",  drafts: 2, awaiting: 0, published: 3, leads: 38, views:  9200, last: "Yesterday" },
  { id: "faridah",  drafts: 0, awaiting: 1, published: 7, leads: 52, views: 17400, last: "3d ago" },
];

export const TOPICS: Array<{ tag: string; kind: KindKey; count: number; leads: number; trend: string }> = [
  { tag: "URA Master Plan",  kind: "URA",    count: 12, leads: 84, trend: "+18%" },
  { tag: "ABSD",             kind: "Policy", count:  9, leads: 62, trend: "+22%" },
  { tag: "HDB",              kind: "Policy", count: 14, leads: 71, trend: "+9%"  },
  { tag: "Private resale",   kind: "Market", count: 11, leads: 58, trend: "-3%"  },
  { tag: "New launch",       kind: "Market", count:  8, leads: 49, trend: "+12%" },
  { tag: "CPF",              kind: "Policy", count:  6, leads: 31, trend: "+4%"  },
  { tag: "En-bloc",          kind: "Brief",  count:  5, leads: 18, trend: "-8%"  },
  { tag: "Cooling measures", kind: "Brief",  count:  7, leads: 42, trend: "+33%" },
  { tag: "JLD",              kind: "URA",    count:  6, leads: 38, trend: "+15%" },
  { tag: "GSW",              kind: "URA",    count:  4, leads: 22, trend: "+8%"  },
  { tag: "IRAS",             kind: "Policy", count:  8, leads: 41, trend: "+6%"  },
  { tag: "Rental",           kind: "Market", count:  4, leads: 14, trend: "-12%" },
];

export type AuditEvent = {
  who: "you" | "system" | "hwee" | AuthorKey;
  when: string;
  kind: "editorial" | "dataengine" | "accounts";
  text: string;
  target: string | null;
  severity?: "error";
};

export const AUDIT: AuditEvent[] = [
  { who: "you",     when: "Today · 09:42",     kind: "editorial",  text: "Approved & published “URA Draft Master Plan 2025”", target: "/news/ura-master-plan-2025" },
  { who: "you",     when: "Today · 09:08",     kind: "editorial",  text: "Commented on “Q1 2026 private resale” v3 — § Jurong Lake District", target: "a-2026-05-12-a" },
  { who: "system",  when: "Today · 09:00",     kind: "dataengine", text: "Job ura-master-plan completed: 18,420 rows upserted", target: null },
  { who: "marcus",  when: "Today · 08:14",     kind: "editorial",  text: "Submitted v3 of “Q1 2026 private resale”", target: "a-2026-05-12-a" },
  { who: "system",  when: "Today · 04:12",     kind: "dataengine", text: "Job iras-stamp-duty failed: 401 from /iras-api", target: null, severity: "error" },
  { who: "you",     when: "Yesterday · 22:30", kind: "accounts",   text: "Suspended user marc.dubois@erareal.com", target: "u_4124" },
  { who: "jianwei", when: "Yesterday · 18:02", kind: "editorial",  text: "Submitted v2 of “ABSD remission walk-through”", target: "a-2026-05-12-b" },
  { who: "priya",   when: "Yesterday · 14:45", kind: "editorial",  text: "Submitted v2 of “MOP changes are coming”", target: "a-2026-05-13-b" },
  { who: "hwee",    when: "Yesterday · 11:20", kind: "accounts",   text: "Promoted user lee.suling@huttons.sg to Pro tier", target: "u_4121" },
  { who: "system",  when: "Yesterday · 09:00", kind: "dataengine", text: "Backfill triggered for ura-master-plan (admin: hwee)", target: null },
];
